import { useCallback, useEffect, useRef } from 'react';
import { useLazyValidateIntegrationQuery } from '@ibk/shared-config';
import { clearIntegrationData, getIntegrationData, saveIntegrationData } from '../../utils/integrationStorage';
export function useIntegrationData(personAccessData, options = {}) {
    const { enabled = true, selectedEntityId } = options;
    const [validateIntegration, { isLoading }] = useLazyValidateIntegrationQuery();
    const errorsRef = useRef({});
    const processedEntitiesRef = useRef(new Set());
    const fetchIntegrationForEntity = useCallback(async (entity, forceRefresh = false) => {
        const entityId = entity.entity.id;
        const partyId = entity.entity.party.id;
        if (forceRefresh) {
            processedEntitiesRef.current.delete(entityId);
        }
        if (processedEntitiesRef.current.has(entityId) && !forceRefresh) {
            return;
        }
        const existingData = getIntegrationData(entityId);
        if (existingData && !forceRefresh) {
            processedEntitiesRef.current.add(entityId);
            return;
        }
        try {
            const result = await validateIntegration({ partyId }).unwrap();
            saveIntegrationData(entityId, result);
            processedEntitiesRef.current.add(entityId);
            errorsRef.current[entityId] = null;
        }
        catch (error) {
            errorsRef.current[entityId] = error;
            processedEntitiesRef.current.add(entityId);
        }
    }, [validateIntegration]);
    const validateActiveEntity = useCallback(async (entityId) => {
        if (!personAccessData || !entityId) {
            return;
        }
        const activeEntity = personAccessData.find(item => (item.entityType === 'organization' || item.entityType === 'person') && item.entity.id === entityId);
        if (!activeEntity) {
            return;
        }
        processedEntitiesRef.current.delete(entityId);
        clearIntegrationData(entityId);
        await fetchIntegrationForEntity(activeEntity, true);
        const validationError = errorsRef.current[entityId];
        if (validationError) {
            throw validationError;
        }
    }, [personAccessData, fetchIntegrationForEntity]);
    const validateSelectedCompany = useCallback(async (entityId) => {
        if (!personAccessData) {
            return;
        }
        processedEntitiesRef.current.delete(entityId);
        clearIntegrationData(entityId);
        const selectedOrganization = personAccessData.find(item => item.entityType === 'organization' && item.entity.id === entityId);
        if (!selectedOrganization) {
            return;
        }
        await fetchIntegrationForEntity(selectedOrganization, true);
    }, [personAccessData, fetchIntegrationForEntity]);
    useEffect(() => {
        if (!enabled || !personAccessData || isLoading || !selectedEntityId) {
            return;
        }
        const selectedOrganization = personAccessData.find(item => item.entityType === 'organization' && item.entity.id === selectedEntityId);
        if (!selectedOrganization) {
            return;
        }
        fetchIntegrationForEntity(selectedOrganization);
    }, [personAccessData, enabled, fetchIntegrationForEntity, isLoading, selectedEntityId]);
    return {
        isLoading,
        errors: errorsRef.current,
        validateSelectedCompany,
        validateActiveEntity,
    };
}
